import type { Ambiente } from '@plane/shared';

import { TituloComDestaque } from '@/components/titulo-com-destaque';
import { useAmbientes } from '@/hooks/use-conteudo';
import { useRevelarAoRolar } from '@/hooks/use-revelar-ao-rolar';
import { Miniatura, useMiniaturaQueSegue } from '@/modules/home/components/miniatura-que-segue';

/** Escalonamento entre as linhas, para elas entrarem em cascata. */
const PASSO_MS = 45;

function revelacao(visivel: boolean): string {
  return `transition-[opacity,transform] duration-[900ms] ease-plane motion-reduce:transition-none ${
    visivel ? 'translate-y-0 opacity-100' : 'translate-y-[26px] opacity-0'
  }`;
}

/**
 * Uma linha da lista.
 *
 * O fundo que acende no hover é um elemento sangrando `100vw` para os dois
 * lados — é o que faz a faixa atravessar a tela inteira, e não só a largura do
 * texto. Vive atrás do conteúdo (`-z-10`) e é a seção que o corta, com
 * `overflow-hidden`.
 */
function Linha({
  ambiente,
  numero,
  ancora,
  visivel,
  atraso,
  aoEntrar,
}: {
  ambiente: Ambiente;
  numero: string;
  ancora: string;
  visivel: boolean;
  atraso: number;
  aoEntrar: (foto: string | null) => void;
}) {
  return (
    <li
      style={{ transitionDelay: `${String(atraso)}ms` }}
      className={`border-b border-bone/15 ${revelacao(visivel)}`}
      onPointerEnter={() => aoEntrar(ambiente.foto)}
    >
      <a
        href={ancora}
        // O foco por teclado mostra a mesma foto que o mouse mostraria: quem
        // navega tabulando recebe a mesma informação de quem aponta.
        onFocus={() => aoEntrar(ambiente.foto)}
        className="group relative grid grid-cols-[auto_1fr_auto] items-center gap-[clamp(14px,3vw,40px)] py-[clamp(20px,3vw,34px)] transition-[padding,color] duration-500 ease-plane-slow hover:px-[clamp(10px,2vw,26px)] motion-reduce:transition-none min-[980px]:grid-cols-[auto_1fr_auto_auto]"
      >
        <span
          aria-hidden="true"
          className="absolute inset-y-0 right-[-100vw] left-[-100vw] -z-10 bg-brand-800 opacity-0 transition-opacity duration-[450ms] ease-plane group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none"
        />

        <span className="text-[0.66rem] tracking-[0.16em] text-brass tabular-nums">{numero}</span>

        <span className="font-display text-[clamp(1.6rem,4.4vw,3rem)] leading-none font-light tracking-[-0.02em]">
          {ambiente.nome}
        </span>

        {ambiente.detalhe !== '' && (
          <span className="hidden text-[0.8rem] tracking-[0.02em] text-bone/50 min-[980px]:block">
            {ambiente.detalhe}
          </span>
        )}

        <span
          aria-hidden="true"
          className="translate-x-[-10px] text-[1.2rem] text-brass opacity-0 transition-all duration-[450ms] ease-plane-slow group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100 motion-reduce:transition-none"
        >
          →
        </span>
      </a>
    </li>
  );
}

/**
 * Seção 02 — Ambientes.
 *
 * Portada de `legacy/static-site/index.html`, seção `#ambientes`, e agora com o
 * conteúdo vindo do painel — inclusive a foto de cada linha, que alimenta a
 * miniatura acompanhando o cursor.
 *
 * A numeração sai da posição na lista, como no processo: editável, permitiria
 * dois "02" e a ordem contradizer o que está escrito.
 */
export function AmbientesSection() {
  const ambientes = useAmbientes();
  const { ref, visivel } = useRevelarAoRolar<HTMLElement>();
  const miniatura = useMiniaturaQueSegue();

  return (
    <section
      ref={ref}
      id="ambientes"
      className="overflow-hidden bg-brand-950 py-[clamp(70px,10vw,140px)] text-bone"
      aria-labelledby="ambientes-titulo"
    >
      <div className="mx-auto w-full max-w-[1360px] px-6">
        <header
          className={`mb-[clamp(44px,6vw,84px)] flex flex-wrap items-end justify-between gap-[clamp(24px,5vw,80px)] ${revelacao(visivel)}`}
        >
          <div>
            <p className="flex items-center gap-2.5 text-[0.72rem] font-medium tracking-[0.22em] text-brass-light uppercase">
              <span
                aria-hidden="true"
                className="inline-block h-px w-[34px] bg-brass-light opacity-75"
              />
              {ambientes.kicker}
            </p>

            <TituloComDestaque
              id="ambientes-titulo"
              texto={ambientes.titulo}
              className="mt-[1.4rem] font-display text-[clamp(2.1rem,5.4vw,4.4rem)] leading-[0.99] tracking-[-0.025em] text-bone"
              destaqueClassName="font-light text-brass-light italic"
            />
          </div>

          {/* Some em quem não tem cursor, como no protótipo: num toque a dica
              não faz sentido. */}
          {ambientes.dica !== '' && (
            <p className="m-0 text-[0.64rem] tracking-[0.24em] text-bone/40 uppercase [@media(hover:none)]:hidden">
              {ambientes.dica}
            </p>
          )}
        </header>

        <ul
          className="m-0 list-none border-t border-bone/15 p-0"
          onPointerMove={miniatura.aoMover}
          onPointerLeave={miniatura.aoSair}
          onBlur={miniatura.aoSair}
        >
          {ambientes.itens.map((ambiente, indice) => (
            <Linha
              key={ambiente.nome}
              ambiente={ambiente}
              numero={String(indice + 1).padStart(2, '0')}
              ancora={ambientes.ancora}
              visivel={visivel}
              atraso={indice * PASSO_MS}
              aoEntrar={miniatura.aoEntrarEm}
            />
          ))}
        </ul>
      </div>

      {miniatura.ativa && (
        <Miniatura refDaBolha={miniatura.bolha} mediaId={miniatura.fotoVisivel} />
      )}
    </section>
  );
}
